const cloneTable = (table) => JSON.parse(JSON.stringify(table || []));

const cellKey = (rowIndex, colIndex) => `${rowIndex}-${colIndex}`;

export default {
  data() {
    return {
      tableEditSessions: {},
      pendingTableEditConversion: false,
    };
  },
  computed: {
    pendingTableEditLabels() {
      return Object.values(this.tableEditSessions || {})
        .filter((session) => session && this.hasTableChanges(session.key))
        .map((session) => session.label);
    },
  },
  methods: {
    ensureTableEditSession(key, label = "") {
      if (!this.tableEditSessions[key]) {
        this.$set(this.tableEditSessions, key, {
          key,
          label: label || "Tabella",
          editing: false,
          baseline: [],
          baselineInitialized: false,
          currentValues: {},
          dirtyCells: {},
          undoStack: [],
        });
      } else if (label && this.tableEditSessions[key].label !== label) {
        this.$set(this.tableEditSessions[key], "label", label);
      }
      return this.tableEditSessions[key];
    },
    getTableEditSession(key) {
      return this.ensureTableEditSession(key);
    },
    isTableEditing(key) {
      return Boolean(this.tableEditSessions[key]?.editing);
    },
    primeTableEditSession(key, label, table, force = false) {
      const session = this.ensureTableEditSession(key, label);
      if (!session.baselineInitialized || force) {
        this.$set(session, "baseline", cloneTable(table));
        this.$set(session, "baselineInitialized", true);
        this.$set(session, "currentValues", {});
        this.$set(session, "dirtyCells", {});
        this.$set(session, "undoStack", []);
      }
      return session;
    },
    startTableEditSession(key, label, table) {
      const session = this.primeTableEditSession(key, label, table);
      if (session.editing) return;
      this.$set(session, "editing", true);
    },
    stopTableEditSession(key) {
      const session = this.ensureTableEditSession(key);
      this.$set(session, "editing", false);
    },
    commitTableEditSession(key, table) {
      const session = this.ensureTableEditSession(key);
      this.$set(session, "editing", false);
      this.$set(session, "baseline", cloneTable(table));
      this.$set(session, "baselineInitialized", true);
      this.$set(session, "currentValues", {});
      this.$set(session, "dirtyCells", {});
      this.$set(session, "undoStack", []);
    },
    toggleTableEditSession(key, label, table) {
      if (this.isTableEditing(key)) {
        this.stopTableEditSession(key);
      } else {
        this.startTableEditSession(key, label, table);
      }
    },
    trackTableCellChange(
      key,
      rowIndex,
      colIndex,
      value,
      table = null,
      label = ""
    ) {
      const session = table
        ? this.primeTableEditSession(key, label, table)
        : this.ensureTableEditSession(key, label);

      const keyCell = cellKey(rowIndex, colIndex);
      const baselineValue = session.baseline?.[rowIndex]?.[colIndex];
      const previousValue = Object.prototype.hasOwnProperty.call(
        session.currentValues,
        keyCell
      )
        ? session.currentValues[keyCell]
        : baselineValue;

      if (previousValue !== value) {
        session.undoStack.push({
          rowIndex,
          colIndex,
          oldValue: previousValue,
          newValue: value,
        });
      }

      this.$set(session.currentValues, keyCell, value);
      if (value !== baselineValue) {
        this.$set(session.dirtyCells, keyCell, true);
      } else {
        this.$delete(session.dirtyCells, keyCell);
      }
    },
    isTableCellDirty(key, rowIndex, colIndex) {
      return Boolean(
        this.tableEditSessions[key]?.dirtyCells?.[cellKey(rowIndex, colIndex)]
      );
    },
    tableCellEditClasses(key, rowIndex, colIndex, invalid = false) {
      const dirty = this.isTableCellDirty(key, rowIndex, colIndex);
      return {
        "cell-error": invalid,
        "error-cell": invalid,
        "modified-cell": dirty && !invalid,
      };
    },
    tableCellDisplayClasses(key, rowIndex, colIndex, invalid = false) {
      const dirty = this.isTableCellDirty(key, rowIndex, colIndex);
      return {
        "cell-display-state": dirty || invalid,
        "cell-display-modified": dirty && !invalid,
        "cell-display-error": invalid,
      };
    },
    hasTableChanges(keyOrSession) {
      const session =
        typeof keyOrSession === "string"
          ? this.tableEditSessions[keyOrSession]
          : keyOrSession;
      return Boolean(session && Object.keys(session.dirtyCells || {}).length);
    },
    hasTableUndo(key) {
      return Boolean(this.tableEditSessions[key]?.undoStack?.length);
    },
    undoLastTableChange(key, applyChange) {
      const session = this.ensureTableEditSession(key);
      const change = session.undoStack.pop();
      if (!change) return;
      applyChange(change);

      const keyCell = cellKey(change.rowIndex, change.colIndex);
      this.$set(session.currentValues, keyCell, change.oldValue);
      const baselineValue =
        session.baseline?.[change.rowIndex]?.[change.colIndex];
      if (change.oldValue !== baselineValue) {
        this.$set(session.dirtyCells, keyCell, true);
      } else {
        this.$delete(session.dirtyCells, keyCell);
      }
    },
    resetTableEditSession(key, tableName, afterRestore) {
      const session = this.ensureTableEditSession(key);
      const restored = cloneTable(session.baseline);
      this.$set(this, tableName, restored);
      this.$set(session, "currentValues", {});
      this.$set(session, "dirtyCells", {});
      this.$set(session, "undoStack", []);
      if (typeof afterRestore === "function") {
        afterRestore(restored);
      }
    },
    clearAllTableEditSessions() {
      Object.keys(this.tableEditSessions || {}).forEach((key) => {
        const session = this.tableEditSessions[key];
        this.$set(session, "editing", false);
        this.$set(session, "baselineInitialized", false);
        this.$set(session, "currentValues", {});
        this.$set(session, "dirtyCells", {});
        this.$set(session, "undoStack", []);
      });
    },
    commitPendingTableEdits() {
      Object.keys(this.tableEditSessions || {}).forEach((key) => {
        const session = this.tableEditSessions[key];
        this.$set(session, "editing", false);
        this.$set(session, "currentValues", {});
        this.$set(session, "dirtyCells", {});
        this.$set(session, "undoStack", []);
      });
    },
  },
};
